// SPDX-License-Identifier: Apache-2.0
import GirafeHTMLElement from '../../base/GirafeHTMLElement';

import Basemap from '../../models/basemaps/basemap';
import BaseLayer from '../../models/layers/baselayer';
import Layer from '../../models/layers/layer';
import LayerWms from '../../models/layers/layerwms';
import { CameraConfig } from '../../tools/state/globe';
import { CellContent } from '../../tools/state/layout';
import { Callback } from '../../tools/state/statemanager';

import type Globe from './tools/globe';

/**
 * The 3D Map.
 *
 * The Component itself stays lightweight: Cesium and OLCesium are only imported when the 3D Map
 * is displayed for the first time. Everything that needs those libraries lives in ./tools/globe.
 * The synchronization with the 2D Map is done by OLCesium, through the OpenLayers map of the
 * MapManager. There is therefore no direct dependency to the map-2d Component.
 */
export default class Map3dComponent extends GirafeHTMLElement {
  templateUrl = './template.html';
  styleUrls = ['../../styles/common.css', './style.css'];

  globe: Globe | null = null;
  map3dTarget!: HTMLDivElement;
  loading: boolean = false;
  pixelTolerance = 10;

  /** Promise of the currently running globe creation, to prevent creating it twice. */
  private globeCreation: Promise<void> | null = null;

  /** The OLCesium instance, once the globe has been created. */
  public get map3d() {
    return this.globe?.olCesium;
  }

  private get config() {
    return this.context.configManager.Config;
  }

  public constructor() {
    super('map-3d');
  }

  /**
   *
   * @param path Overridden to make those methods public for this component
   * @param callback
   */
  public override subscribe(path: string, callback: Callback): Callback;
  public override subscribe(path: RegExp, callback: Callback): Callback;
  public override subscribe(path: string | RegExp, callback: Callback): Callback {
    // @ts-expect-error The call would have succeeded against this implementation,
    // but implementation signatures of overloads are not externally visible.
    return super.subscribe(path, callback);
  }

  /**
   * The Map needs to be displayed in Fullscreen Mode.
   */
  protected override get isFullscreenComponent(): boolean {
    return true;
  }

  protected override connectedCallback() {
    super.connectedCallback();
    this.render();
    this.registerEvents();
    this.onGlobeToggled();
  }

  override render() {
    super.render();
    this.map3dTarget = this.shadow.getElementById('cs-map') as HTMLDivElement;
  }

  private registerEvents() {
    this.subscribe('layout.cells', async () => {
      await this.onGlobeToggled();
      this.globe?.setCamera(this.state.globe.camera);
    });
    this.subscribe('globe.shadows', (_oldShadows: boolean, newShadows: boolean) => this.globe?.setShadows(newShadows));
    this.subscribe('globe.camera', async (_oldCamera: CameraConfig, newCamera: CameraConfig) => {
      await this.onGlobeToggled();
      this.globe?.setCamera(newCamera);
    });

    this.subscribe('activeBasemaps', (_: Basemap[], newBasemaps: Basemap[]) =>
      this.globe?.onChangeBasemaps(newBasemaps)
    );
    this.subscribe(/activeBasemaps\.\d\.opacity/, (_oldOpacity: number, _newOpacity: number, basemap: Basemap) =>
      this.globe?.changeBasemapOpacity(basemap)
    );

    this.subscribe(/layers\.layersList\..*\.activeState/, (_oldActive: boolean, _newActive: boolean, layer: Layer) => {
      if (layer instanceof LayerWms) {
        this.globe?.onLayerToggled(layer);
      }
    });
    this.subscribe(/layers\.layersList\..*\.opacity/, (_oldOpacity: number, _newOpacity: number, layer: BaseLayer) =>
      this.globe?.changeOpacity(layer)
    );
    this.subscribe(/layers\.layersList\..*\.filter/, (_oldFilter: string, _newFilter: string, layer: LayerWms) =>
      this.globe?.changeFilter(layer)
    );

    this.subscribe('application.isReady', (_: boolean, isLoaded: boolean) => {
      if (isLoaded) {
        // The globe may not be ready when the rest of the application is initialized.
        // The camera of a shared state can only be applied once everything is loaded.
        this.globe?.setCamera(this.state.globe.camera);
      }
    });
  }

  /**
   * Lazily loads Cesium/OLCesium and creates the globe. Does nothing if no 3D configuration
   * is available or if the globe has already been created.
   */
  private async createGlobe(): Promise<void> {
    if (this.globe || !this.config.map3d) {
      return;
    }
    this.globeCreation ??= (async () => {
      this.loading = true;
      this.refreshRender();

      const { default: Globe } = await import('./tools/globe');
      const globe = new Globe(this.context, this.name, this.pixelTolerance);
      await globe.initialize(this.map3dTarget);
      this.globe = globe;

      this.loading = false;
      this.refreshRender();
      this.state.globe.loaded = true;
    })();

    return this.globeCreation;
  }

  /**
   * Creates and enables the globe when the 3D Map is displayed in a Cell of the Grid,
   * and disables it when it is not displayed anymore.
   * The size and the position of the Map are managed by the Grid, OLCesium takes care
   * of following the size of its container.
   */
  async onGlobeToggled(): Promise<void> {
    const isDisplayed = (this.state.layout.cells as CellContent[]).includes('map-3d');
    if (isDisplayed) {
      await this.createGlobe();
      this.globe?.setEnabled(true);
    } else {
      this.globe?.setEnabled(false);
      this.unregisterInteractionListeners('globe.select');
    }
  }
}
